interface SparkbarProps {
  values: number[];
  width?: number;
  height?: number;
  ariaLabel: string;
  color?: string;
  className?: string;
}

/**
 * Tiny inline sparkbar — companion to {@link Sparkline} for discrete daily
 * counts. Renders one bar per value, scaled to the series max so the shape
 * (week pattern) is preserved without an axis or grid.
 *
 * Bars are full-width within their slot (1px gap between). All-zero series
 * renders flat baseline. A `<svg role='img'>` carries the aria-label.
 */
export function Sparkbar({
  values,
  width = 96,
  height = 24,
  ariaLabel,
  color = 'hsl(var(--primary))',
  className,
}: SparkbarProps) {
  if (values.length === 0) return null;

  const max = Math.max(...values, 0);
  const gap = 1;
  const slotWidth = width / values.length;
  const barWidth = Math.max(1, slotWidth - gap);
  // Leave 1px breathing room at the top so a max-height bar doesn't touch the edge.
  const pad = 1;
  const innerH = height - pad;

  return (
    <svg
      role='img'
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='none'
      className={className}
    >
      {values.map((v, i) => {
        const h = max > 0 ? (v / max) * innerH : 0;
        const x = i * slotWidth;
        const y = height - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={Math.max(h, max > 0 && v > 0 ? 1 : 0)}
            fill={color}
            opacity={v === 0 ? 0.2 : 1}
          />
        );
      })}
    </svg>
  );
}
